'use client';
import request from '@nineeng/request';
import z from 'zod';

type HttpError = typeof Error & {
	status?: number;
	details?: string[];
	body?: {
		details?: string[];
	};
};

type Options = Record<string, unknown>;

// Errors that should not trigger a redirect
const errorsNotToHandle = [400, 405, 409, 423];

const errorHandler = (error: HttpError, url: string, method: string) => {
	if (error.status === 401) {
		const currentUrl = new URL(window.location.href);
		const hasErrorParams = ['occurredAt', 'method', 'uuid'].every((param) => currentUrl.searchParams.has(param));

		window.location.replace(
			`/authentication/login${hasErrorParams ? '' : `?from=${encodeURIComponent(window.location.href)}`}`,
		);
		return;
	}

	if (error.status === 403) {
		const details = error.body?.details || [];
		window.location.replace(
			`/403?permissions=${details?.join(',')}&occurredAt=${encodeURIComponent(url)}&method=${encodeURIComponent(method)}`,
		);
		return;
	}

	if (error.status === 404) {
		window.location.replace(`/404?occurredAt=${encodeURIComponent(url)}&method=${encodeURIComponent(method)}`);
		return;
	}

	if (error.status && errorsNotToHandle.includes(error.status)) {
		return;
	}

	window.location.replace(
		`/error?statusCode=${error.status}&occurredAt=${encodeURIComponent(url)}&method=${encodeURIComponent(method)}`,
	);
	// return;
};

export const guardedGet = async <S extends z.ZodSchema | undefined, T = S extends z.ZodSchema ? z.infer<S> : unknown>(
	url: string,
	options: { schema: S } & Omit<Options, 'schema'>,
) => {
	let response: Awaited<ReturnType<typeof request.get<T>>>;

	const optionsToUse = { ...options };
	delete optionsToUse.schema;

	try {
		response = await request.get<T>(url, optionsToUse);
	} catch (error) {
		console.error(error);
		errorHandler(error as HttpError, url, 'GET');
		throw error;
	}

	// Currently we check for an undefined `options` parameter even though it's typed as `OptionsWithSchema`.
	// This is because we want Typescript to error everywhere a schema isn't passed yet, but we don't want
	// to add the `schema` property to every request usage, it should come gradually for time efficiency.
	if (options?.schema) {
		try {
			options.schema.parse(response.body);
		} catch (error) {
			// console.error(error);
		}
	}

	return response;
};

export const guardedPost = async <S extends z.ZodSchema | undefined, T = S extends z.ZodSchema ? z.infer<S> : unknown>(
	url: string,
	body: any,
	options?: { schema: S } & Omit<Options, 'schema'>,
) => {
	let response: Awaited<ReturnType<typeof request.post<T>>>;

	const optionsToUse = { ...options };
	delete optionsToUse.schema;
	try {
		response = await request.post<T>(url, body, optionsToUse);
	} catch (error) {
		console.error(error);
		errorHandler(error as HttpError, url, 'POST');
		throw error;
	}

	// Currently we check for an undefined `options` parameter even though it's typed as `OptionsWithSchema`.
	// This is because we want Typescript to error everywhere a schema isn't passed yet, but we don't want
	// to add the `schema` property to every request usage, it should come gradually for time efficiency.
	if (options?.schema) {
		try {
			options.schema.parse(response.body);
		} catch (error) {
			console.error(error);
		}
	}

	return response;
};

export const guardedPut = async <S extends z.ZodSchema | undefined, T = S extends z.ZodSchema ? z.infer<S> : unknown>(
	url: string,
	body: any,
	options?: { schema: S } & Omit<Options, 'schema'>,
) => {
	let response: Awaited<ReturnType<typeof request.post<T>>>;

	const optionsToUse = { ...options };
	delete optionsToUse.schema;

	try {
		response = await request.put<T>(url, body, optionsToUse);
	} catch (error) {
		console.error(error);
		errorHandler(error as HttpError, url, 'PUT');
		throw error;
	}

	// Currently we check for an undefined `options` parameter even though it's typed as `OptionsWithSchema`.
	// This is because we want Typescript to error everywhere a schema isn't passed yet, but we don't want
	// to add the `schema` property to every request usage, it should come gradually for time efficiency.
	if (options?.schema) {
		try {
			options.schema.parse(response.body);
		} catch (error) {
			console.error(error);
		}
	}

	return response;
};

export const guardedDelete = async <T>(url: string, options = {}) => {
	let response: Awaited<ReturnType<typeof request.del<T>>>;
	try {
		response = await request.del<T>(url, options);
	} catch (error) {
		console.error(error);
		errorHandler(error as HttpError, url, 'DELETE');
		throw error;
	}

	return response;
};

const guardedClient = {
	get: guardedGet,
	post: guardedPost,
	put: guardedPut,
	del: guardedDelete,
};

export default guardedClient;
